/**
 * The active route scope (the product and repo ULIDs of the mounted page),
 * mirrored into a tiny store so shell surfaces outside the route tree — the
 * ⌘K palette's navigation entries, mainly — can build scoped links. Written
 * by the route layouts in routes.tsx (ProductScope, RepoSettingsScope) and
 * by SessionPage; read-only everywhere else.
 */

import { useParams } from 'react-router';
import { create } from 'zustand';

import type { Ulid } from '../api/types';

/**
 * The route ids. `repoId` scopes artifacts / status / the runtime map;
 * `productId` scopes sessions. On the codebase page
 * (`/products/:pid/codebases/:rid`) both ride the path; a conversation
 * (`/products/:pid/chats/:sid`) has no `:rid`, so the repo SessionPage
 * resolved (the remembered one when it still belongs to the product, else
 * the first) is read from the store instead. Components mounted outside any
 * workspace route get empty strings — their data hooks stay disabled.
 */
export function useWorkspaceIds(): { repoId: Ulid; productId: Ulid } {
  const params = useParams();
  const scopedRepoId = useRouteScope((s) => s.repoId);
  return {
    repoId: params['rid'] ?? scopedRepoId ?? '',
    productId: params['pid'] ?? '',
  };
}

export interface RouteScopeState {
  productId: Ulid | null;
  repoId: Ulid | null;
  setProductScope: (pid: Ulid | null) => void;
  setRepoScope: (rid: Ulid | null) => void;
}

export const useRouteScope = create<RouteScopeState>((set) => ({
  productId: null,
  repoId: null,
  setProductScope(pid) {
    set({ productId: pid });
  },
  setRepoScope(rid) {
    set({ repoId: rid });
  },
}));
